import React, { useContext, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Container,
  Divider,
  Tab,
  Tabs,
} from '@mui/material';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Matchup, Pick, User } from 'src/models';
import { db, functions } from 'src/services/firebase-service';
import logging from 'src/services/logging';
import AppCycleTab from 'src/pages/Admin/AppCycleTab';
import SeederTab from 'src/pages/Admin/SeederTab';
import { CycleContext } from 'src/context/CycleContext';

type TabPanelProps = {
  children?: React.ReactNode;
  index: number;
  value: number;
};

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 0 }}>{children}</Box>}
    </div>
  );
};

const a11yProps = (index: number) => {
  return {
    id: `simple-tab-${index}`,
    'aria-controls': `simple-tabpanel-${index}`,
  };
};

const AdminPage = () => {
  const cycle = useContext(CycleContext);
  const [activeTab, setActiveTab] = useState(0);
  const [calling, setIsCalling] = useState<boolean>(false);
  const [data, setData] = useState<User[]>();
  const [usersDb, setUsersDb] = useState<User[]>();
  const [roundMatchups, setRoundMatchups] = useState<any[]>();
  const [picks, setPicks] = useState<Pick[]>();
  const [pick, setPick] = useState<Pick>();
  const [gameResults, setGameResults] = useState<any[]>();

  // year selector

  // after choosing year get all matchup data and store into firebase

  // store users in db with their uid as the document id
  // so can reference like doc(db, 'users/{user.uid}')

  const callAllUsers = async () => {
    const allUsers = httpsCallable(functions, 'get-users');
    setIsCalling(true);
    const response = await allUsers();
    const data = response.data as User[];
    setIsCalling(false);
    logging.info(`${response.data}`, 'AdminPage');
    setData(data);
  };

  const getUsersDb = async () => {
    try {
      setIsCalling(true);
      const q = query(collection(db, 'users'));
      const snapshot = await getDocs(q);
      const users: User[] = [];
      snapshot.forEach((user) => {
        const data = user.data() as User;
        users.push({
          email: data.email,
          fullName: data.fullName,
          isHokage: data.isHokage,
          providerId: data.providerId,
          uid: data.uid,
        });
      });
      setUsersDb(users);
    } catch (error) {
      logging.error(error, 'AdminPage');
    } finally {
      setIsCalling(false);
    }
  };

  const getRoundMatchups = async () => {
    try {
      setIsCalling(true);
      const q = query(
        collection(db, 'matchups'),
        where('season', '==', cycle.season),
        where('playoffRound', '==', 1)
      );
      const snapshot = await getDocs(q);
      const matchups: Matchup[] = [];
      snapshot.forEach((match) => {
        matchups.push({
          // endTime: match.data().endTime,
          gamePk: match.data().gamePk,
          playoffRound: match.data().playoffRound,
          playoffSeriesLetter: match.data().playoffSeriesLetter,
          season: match.data().season,
          startTime: match.data().startTime,
          teams: match.data().teams,
        });
      });
      setRoundMatchups(matchups);
    } catch (error) {
      logging.error(error, 'AdminPage');
    } finally {
      setIsCalling(false);
    }
  };

  const getPicks = async () => {
    try {
      setIsCalling(true);
      // const q = collection(db, `picks/${dbUser?.uid}/20182019`);
      const q = query(
        collection(db, 'picks'),
        where('season', '==', cycle.season)
      );
      const snapshot = await getDocs(q);
      const picks: Pick[] = [];
      snapshot.forEach((pick) => {
        picks.push({
          uid: pick.data().uid,
          gamePk: pick.data().gamePk,
          playoffRound: pick.data().playoffRound,
          playoffSeriesLetter: pick.data().playoffSeriesLetter,
          season: pick.data().season,
          awayPlayer: pick.data().awayPlayer,
          homePlayer: pick.data().homePlayer,
          numOfGames: pick.data().numOfGames,
          score1: pick.data().score1,
          score2: pick.data().score2,
          team: pick.data().team,
        });
      });
      setPicks(picks);
    } catch (error) {
      logging.error(error, 'AdminPage');
    } finally {
      setIsCalling(false);
    }
  };

  const getPick = async () => {
    try {
      setIsCalling(true);
      const q = query(
        collection(db, 'picks'),
        where('gamePk', '==', 2018030181)
      );
      const snapshot = await getDocs(q);
      const data = snapshot.docs[0];
      setPick({
        uid: data.data().uid,
        gamePk: data.data().gamePk,
        playoffRound: data.data().playoffRound,
        playoffSeriesLetter: data.data().playoffSeriesLetter,
        season: data.data().season,
        awayPlayer: data.data().awayPlayer,
        homePlayer: data.data().homePlayer,
        numOfGames: data.data().numOfGames,
        score1: data.data().score1,
        score2: data.data().score2,
        team: data.data().team,
      });
    } catch (error) {
      logging.error(error, 'AdminPage');
    } finally {
      setIsCalling(false);
    }
  };

  const getAllGameResults = async () => {
    try {
      setIsCalling(true);
      const q = collection(db, 'gameResults');
      const snapshot = await getDocs(q);
      const gameResults: any[] = [];
      snapshot.forEach((game) => {
        gameResults.push({ ...game.data() });
      });
      setGameResults(gameResults);
    } catch (error) {
      logging.error(error, 'AdminPage');
    } finally {
      setIsCalling(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  return (
    <Container maxWidth='sm' sx={{ mt: 3 }}>
      <Card>
        <CardHeader
          title='Hokage Page'
          titleTypographyProps={{ flex: '1 1 100%', variant: 'h6' }}
        />
        <Divider />
        <CardContent>
          <Tabs
            variant='fullWidth'
            value={activeTab}
            onChange={handleTabChange}
            aria-label='tabs-hokage-page'
          >
            <Tab label='App Cycle' {...a11yProps(0)} />
            <Tab label='Seeder' {...a11yProps(1)} />
            <Tab label='Users' {...a11yProps(2)} />
            <Tab label='Test' {...a11yProps(3)} />
          </Tabs>
          <TabPanel value={activeTab} index={0}>
            <AppCycleTab />
          </TabPanel>
          <TabPanel value={activeTab} index={1}>
            <SeederTab />
          </TabPanel>
          <TabPanel value={activeTab} index={2}>
            User Settings
          </TabPanel>
          <TabPanel value={activeTab} index={3}>
            <Button onClick={callAllUsers}>
              {calling ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Call AllUsers'
              )}
            </Button>
            <pre>{JSON.stringify(data, null, 2)}</pre>
            <Button onClick={getUsersDb}>
              {calling ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Call AllUsers DB'
              )}
            </Button>
            <pre>{JSON.stringify(usersDb, null, 2)}</pre>
            <Button onClick={getRoundMatchups}>
              {calling ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Get Round Matchups'
              )}
            </Button>
            <pre>{JSON.stringify(roundMatchups, null, 2)}</pre>
            <Button onClick={getPicks}>
              {calling ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Get All Picks'
              )}
            </Button>
            <pre>{JSON.stringify(picks, null, 2)}</pre>
            <Button onClick={getPick}>
              {calling ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Get A Pick'
              )}
            </Button>
            <pre>{JSON.stringify(pick, null, 2)}</pre>
            <Button onClick={getAllGameResults}>
              {calling ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                'Get All Game Results'
              )}
            </Button>
            <pre>{JSON.stringify(gameResults, null, 2)}</pre>
          </TabPanel>
        </CardContent>
      </Card>
    </Container>
  );
};

export default AdminPage;
